import { BreedSelect } from "@/components/pawkt/BreedSelect";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { petDateErrors, todayISO } from "@/lib/phone";
import { PetHealthFields } from "@/components/pawkt/VaccinationRecord";
import { PetAgeField } from "@/components/pawkt/PetAgeField";

const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500";

/* Multi-pet repeater — add several pets in one customer workflow. Each pet is an
   independent record (never a blob). Reuses the canonical species-aware BreedSelect. */
export function PetsRepeater({ value = [], onChange }) {
  const pets = value || [];
  const upd = (i, key, val) => onChange(pets.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));
  const add = () => onChange([...pets, { name: "", species: "Dog", species_id: "dog", breed: "", gender: "" }]);
  const rm = (i) => onChange(pets.filter((_, idx) => idx !== i));
  const btnLabel = pets.length === 0 ? "Add Pet" : "Add Another Pet";

  return (
    <div className="space-y-3" data-testid="pets-repeater">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pets (optional) · each new pet needs age, spay/neuter status + vaccination record</div>
      {pets.length === 0 && (
        <p className="text-sm text-slate-400" data-testid="pets-empty">No pets added yet.</p>
      )}
      {pets.map((p, i) => {
        const de = petDateErrors(p);
        return (
          <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-2" data-testid={`pet-entry-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">Pet {i + 1}</span>
              <button type="button" onClick={() => rm(i)} data-testid={`remove-pet-${i}`}
                className="text-red-500 hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
            </div>
            <input value={p.name || ""} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Pet name"
              data-testid={`pet-name-${i}`} className={inputCls} />
            <BreedSelect v={p} set={(k, val) => upd(i, k, val)}
              setMany={(patch) => onChange(pets.map((pp, idx) => (idx === i ? { ...pp, ...patch } : pp)))} />
            <select value={p.gender || ""} onChange={(e) => upd(i, "gender", e.target.value)} data-testid={`pet-gender-${i}`} className={inputCls}>
              <option value="">Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11px] text-slate-500">Date of birth (optional)
                <input type="date" max={todayISO()} value={p.dob || ""} onChange={(e) => upd(i, "dob", e.target.value)} data-testid={`pet-dob-${i}`} className={inputCls} />
              </label>
              <label className="text-[11px] text-slate-500">Adoption date (optional)
                <input type="date" max={todayISO()} value={p.adoption_date || ""} onChange={(e) => upd(i, "adoption_date", e.target.value)} data-testid={`pet-adoption-${i}`} className={inputCls} />
              </label>
            </div>
            {(de.dob || de.adoption_date) && <p className="text-xs text-rose-600" data-testid={`pet-date-error-${i}`}>{de.dob || de.adoption_date}</p>}
            <PetAgeField value={p} testid={`pet-age-${i}`} showError={(p.name || "").trim() !== "" && (p.age_years != null || p.age_months != null)}
              onChange={(patch) => onChange(pets.map((pp, idx) => (idx === i ? { ...pp, ...patch } : pp)))} />
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={!!p.must_board_alone} onChange={(e) => upd(i, "must_board_alone", e.target.checked)} data-testid={`pet-board-alone-${i}`} />
              Must board alone (never shares a kennel/room)
            </label>
            <PetHealthFields pet={p} testid={`pet-health-${i}`} onChange={(patch) => onChange(pets.map((pp, idx) => (idx === i ? { ...pp, ...patch } : pp)))} />
          </div>
        );
      })}
      <Button type="button" variant="outline" onClick={add} data-testid="add-another-pet" className="gap-1.5 w-full">
        <Plus className="h-4 w-4" /> {btnLabel}
      </Button>
    </div>
  );
}
